import { Button } from '@components/button';
import { Card } from '@components/card';
import { useDeleteModal } from '@components/generic-modals';
import { IconEdit } from '@components/icons/edit';
import { IconEmailAddress } from '@components/icons/email-address';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconTrash } from '@components/icons/trash';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextStatusIndicator } from '@components/text-status-indicator';
import { useScopedTranslate } from '@locale/translate';

import { useDeleteDnsDomain, useVerifyDnsDomain } from 'api/company/hooks';
import { EmailDomain } from 'api/company/requests';

type DnsDomainCardProps = {
  domain: EmailDomain;
  onEditDomain: () => void;
};

export const DnsDomainCard = ({ domain, onEditDomain }: DnsDomainCardProps) => {
  const { t } = useScopedTranslate(locale);
  const showDeleteModal = useDeleteModal();

  const deleteMutation = useDeleteDnsDomain();
  const verifyMutation = useVerifyDnsDomain();

  const isVerified = domain.isVerified;

  const handleDelete = () => {
    showDeleteModal({
      title: t('deleteDomainTitle'),
      description: t('deleteDomainDescription'),
      hasUserConfirmation: true,
      buttonText: t('remove'),
      buttonIcon: <IconTrash />,
      onConfirm: () => deleteMutation.mutate(domain.shortId),
    });
  };

  const handleVerify = () => {
    verifyMutation.mutate(domain.shortId);
  };

  return (
    <Card spaceX={4} spaceY={3} variant="white">
      {verifyMutation.isLoading && <Spinner />}
      <Stack orientation="horizontal" justify="space-between" align="center">
        <Stack orientation="horizontal" space={2} align="center">
          <IconEmailAddress />
          <div>
            <Stack orientation="horizontal" space={1} align="center">
              <Text text={domain.domain} font="semibold12" />
              <Text text="DNS" font="regular12" color="textSecondary" />
            </Stack>
            <TextStatusIndicator
              variant={isVerified ? 'success' : 'pending'}
              text={isVerified ? t('verified') : t('unverified')}
              size="small"
            />
          </div>
        </Stack>
        <Stack orientation="horizontal" space={2} align="center">
          {!isVerified && (
            <Button
              variant="ghost"
              text={t('verifyDns')}
              onClick={handleVerify}
              isDisabled={verifyMutation.isLoading}
            />
          )}
          <Menu buttonProps={{ variant: 'ghost', iconRight: <IconMoreVertical /> }}>
            <Menu.Item label={t('edit')} icon={<IconEdit />} onSelect={onEditDomain} />
            <Menu.Item
              label={t('remove')}
              icon={<IconTrash />}
              onSelect={handleDelete}
              variant="danger"
            />
          </Menu>
        </Stack>
      </Stack>
    </Card>
  );
};

const locale = {
  en: {
    verified: 'Verified',
    unverified: 'Unverified',
    edit: 'Edit',
    remove: 'Remove',
    verifyDns: 'Verify',
    deleteDomainTitle: 'Remove email domain',
    deleteDomainDescription:
      'Are you sure you want to remove this domain? All email addresses using this domain will also be removed.',
  },
};
